import {
  Badge,
  Dropdown,
  IconButton,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { Currency } from '@commenda/prisma';
import { BillingHelper, cn, CurrencyHelper } from '@commenda/utils';
import { useState } from 'react';
import {
  SubscriptionModal,
  SubscriptionModalStep,
} from './modals/SubscriptionModal';
import { PlanCard } from './PlanCard';
import { SubscriptionCardProps, ViewState } from './types';

export const SubscriptionCard = ({
  id,
  title,
  status,
  billingCycle,
  nextPayment,
  plans,
  companyId,
  onSubscriptionDeleted,
  onPlanDeleted,
  initialViewState = 'Open',
  currency = Currency.USD,
}: SubscriptionCardProps) => {
  const [viewState, setViewState] = useState<ViewState>(initialViewState);
  const modalToggle = useToggle(false);
  const [modalStep, setModalStep] = useState<SubscriptionModalStep>('cancel');

  const toggleView = () => {
    setViewState((prev) => (prev === 'Open' ? 'Closed' : 'Open'));
  };

  const isOpen = viewState === 'Open';

  const openModal = (step: SubscriptionModalStep) => {
    setModalStep(step);
    modalToggle.on();
  };

  const closeModal = () => {
    modalToggle.off();
    // Reset to default step when closing
    setModalStep('cancel');
  };

  const planNames = plans.map((plan) => plan.name);

  return (
    <div
      className={cn(
        'border border-primary-200 rounded-xl transition-all overflow-hidden px-3xl py-2xl',
        isOpen ? 'bg-bg-secondary' : 'bg-bg-primary'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggleView}
            className="flex items-center"
          >
            <Icons.ChevronDown
              className={cn(
                'h-5 w-5 transition-transform',
                isOpen && 'transform rotate-180'
              )}
            />
          </button>

          <h2 className="txt-extra-large-bold text-text-primary">
            {title || `Subscription ${id}`}
          </h2>

          <div className="flex items-center gap-2">
            <Badge
              variant={BillingHelper.getSubscriptionStatusBadgeVariant(status)}
              size="sm"
            >
              {BillingHelper.getSubscriptionStatus(status)}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Dropdown
            align="end"
            menu={{
              groups: [
                {
                  items: [
                    {
                      label: 'Cancel subscription',
                      onClick: () => openModal('cancel'),
                    },
                  ],
                },
              ],
            }}
          >
            <IconButton
              icon={<Icons.DotsVertical />}
              size="lg"
              aria-label="More options"
            />
          </Dropdown>
        </div>
      </div>

      <div className="text-sm-regular text-text-tertiary pl-8">
        {billingCycle} • Next payment of{' '}
        {CurrencyHelper.getCurrencyInfo(currency).style}
        {nextPayment.amount} due on {nextPayment.dueDate}
      </div>

      {isOpen && (
        <div className="space-y-4 mt-6 pl-8">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              id={plan.id}
              name={plan.name}
              category={plan.category}
              price={plan.price}
              assignedTo={plan.assignedTo}
              nextPaymentDue={nextPayment.dueDate}
              entitlementId={plan.entitlementId}
              entitlementIndex={plan.entitlementIndex}
              totalEntitlements={plan.totalEntitlements}
              originalProductId={plan.originalProductId}
              companyId={companyId}
              subscriptionId={id}
              onPlanDeleted={onPlanDeleted}
              billingType={currency}
              frequency={plan.frequency}
            />
          ))}
        </div>
      )}

      <SubscriptionModal
        isOpen={modalToggle.value}
        onClose={closeModal}
        initialStep={modalStep}
        title={title || `Subscription ${id}`}
        nextPaymentAmount={nextPayment.amount}
        nextPaymentDate={nextPayment.dueDate}
        planNames={planNames}
        companyId={companyId}
        subscriptionId={id}
        onSubscriptionDeleted={onSubscriptionDeleted}
      />
    </div>
  );
};

export default SubscriptionCard;
